import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import CareButton from "../CareButton";

export default function BaseForm({
  fields,
  onSubmit,
  schema,
  openModal,
  theme = "dark",
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [darkMode] = useState(theme === "dark");
  const watchedValues = watch();

  const enhancedSubmit = (data) => {
    onSubmit(data);
    reset();
  };

  const shouldRender = (field) => {
    if (!field.conditional) return true;
    const { field: depField, value } = field.conditional;
    return watchedValues[depField] === value;
  };

  const getFieldErrorId = (fieldName) => `${fieldName}-error`;

  const baseInputClass = darkMode
    ? "bg-neutral-800 text-slate-50 focus:ring-offset-neutral-900"
    : "bg-neutral-200 text-black focus:ring-offset-white";

  const focusClass =
    "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(enhancedSubmit)(e);
      }}
      className="w-full p-5 md:p-10 text-start"
      noValidate
    >
      {fields.map((field) => {
        if (!shouldRender(field)) return null;

        const hasError = !!errors[field.name];
        const errorId = getFieldErrorId(field.name);

        return (
          <div key={field.name} className="mb-4 text-start">
            {field.type === "checkbox" ? (
              <div className="flex flex-row items-center gap-6 md:gap-3">
                
                <input
                  id={field.name}
                  type="checkbox"
                  {...register(field.name)}
                  aria-invalid={hasError ? "true" : "false"}
                  aria-describedby={hasError ? errorId : undefined}
                  className={`rounded border border-gray-300 bg-white shrink-0 ${focusClass}`}
                />

                {!field.isClickable ? (
                  <label
                    htmlFor={field.name}
                    className="text-sm text-slate-50 cursor-pointer"
                  >
                    {field.label}
                  </label>
                ) : (
                  <div className="flex flex-col md:flex-row md:gap-3 justify-center items-start">
                    <label
                      htmlFor={field.name}
                      className="text-sm text-slate-50 cursor-pointer"
                    >
                      {field.label}
                    </label>

                    <button
                      type="button"
                      onClick={() => openModal?.("consenso")}
                      className={`text-sm underline text-slate-50 ${focusClass}`}
                    >
                      Informativa Privacy
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <label
                htmlFor={field.name}
                className="text-slate-50"
              >
                {field.label}
              </label>
            )}

            {["text", "email", "password", "date"].includes(field.type) && (
              <input
                id={field.name}
                type={field.type}
                {...register(field.name)}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
                className={`w-full ps-2 py-2 mt-1 rounded ${baseInputClass} ${focusClass}`}
                placeholder={field.placeholder}
              />
            )}

            {field.type === "textarea" && (
              <textarea
                id={field.name}
                {...register(field.name)}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
                className={`w-full ps-2 py-2 mt-1 rounded ${baseInputClass} ${focusClass}`}
                placeholder={field.placeholder}
                rows="3"
              />
            )}

            {field.type === "rating" && (
              <div className="flex flex-col gap-1">
                <div
                  role="radiogroup"
                  aria-invalid={hasError ? "true" : "false"}
                  aria-describedby={hasError ? errorId : undefined}
                  className="flex gap-1"
                >
                  {[...Array(field.max || 5)].map((_, i) => {
                    const value = i + 1;
                    const ratingId = `${field.name}-${value}`;

                    return (
                      <label key={value} htmlFor={ratingId} className="cursor-pointer">
                        <input
                          id={ratingId}
                          type="radio"
                          value={value}
                          {...register(field.name)}
                          className="sr-only"
                        />
                        <span
                          className={`text-2xl text-yellow-400 inline-block ${focusClass}`}
                          tabIndex={0}
                        >
                          {value <= (watch(field.name) || 0) ? "★" : "☆"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {field.type === "select" && (
              <select
                id={field.name}
                {...register(field.name)}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
                className={`w-full mt-1 h-10 rounded ${baseInputClass} ${focusClass}`}
              >
                {field.options.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className={darkMode ? "text-slate-50" : "text-black"}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {field.type === "radio" && (
              <div
                role="radiogroup"
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
                className="flex gap-4 mt-1"
              >
                {field.options.map((option) => {
                  const radioId = `${field.name}-${option.value}`;

                  return (
                    <label
                      key={option.value}
                      htmlFor={radioId}
                      className="text-slate-50"
                    >
                      <input
                        id={radioId}
                        type="radio"
                        value={option.value}
                        {...register(field.name)}
                        className={`mr-2 ${focusClass}`}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            )}

            {field.type === "file" && (
              <input
                id={field.name}
                type="file"
                {...register(field.name)}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
                className={`mt-1 w-full text-slate-50 ${focusClass}`}
              />
            )}

            {hasError && (
              <div
                id={errorId}
                className="text-sm text-red-500 mt-1"
              >
                {errors[field.name].message}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex justify-start mt-10">
        <CareButton
          buttonName="Invia"
          dark={true}
          buttonClass=""
          type="submit"
        />
      </div>
    </form>
  );
}